import type { Locale } from "./types"

export const translations = {
  en: {
    // Navigation & App
    dashboard: "Dashboard",
    leads: "Leads",
    contacts: "Contacts",
    pipelines: "Pipelines",
    inbox: "Inbox",
    properties: "Properties",
    quotes: "Quotes",
    calendar: "Calendar",
    scheduleManagement: "Schedule Management",
    routing: "Assignment",
    scoring: "Lead Scoring",
    aiAssistant: "AI Assistant",
    aiPrequalification: "AI Advisor",
    aiRecommendations: "AI Recommendations",
    auditLog: "Audit Log",
    settings: "Settings",
    logout: "Logout",
    reports: "Reports",
    chatWidget: "Chat Widget",
    help: "Help",
    visits: "Visits",
    roles: "Roles",
    permissions: "Permissions",
    templates: "Templates",
    users: "Users",
    medicalConversations: "Medical Conversations",
    postOpFollowup: "Post-Op Follow-up",

    // Reports & Analytics
    reportsPage: {
      title: "Reports & Analytics",
      subtitle: "Advanced visualization ready for Amazon QuickSight",
    },

    // Common
    search: "Search",
    filter: "Filter",
    export: "Export",
    import: "Import",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    view: "View",
    actions: "Actions",
    status: "Status",
    createdAt: "Created",
    updatedAt: "Updated",
    saving: "Saving...",

    // Lead specific
    newLead: "New Lead",
    leadSource: "Source",
    leadStage: "Stage",
    leadOwner: "Owner",
    leadScore: "Score",
    attribution: "Attribution",
    campaign: "Campaign",

    // Property specific
    available: "Available",
    sold: "Sold",
    reserved: "Reserved",
    rented: "Rented",

    // Phase labels
    phase1: "Phase 1",
    phase2: "Phase 2",
    phase3: "Phase 3",

    // Comprehensive lead page translations
    leadsPage: {
      title: "Leads",
      subtitle: "Manage and track your sales leads",
      searchPlaceholder: "Search leads...",
      allStages: "All Stages",
      filterByStage: "Filter by stage",
      tableHeaders: {
        lead: "Lead",
        attribution: "Attribution",
        stage: "Stage",
        owner: "Owner",
        score: "Score",
        created: "Created",
        actions: "Actions",
      },
      unassigned: "Unassigned",
      dropdownActions: {
        viewDetails: "View Details",
        editLead: "Edit Lead",
        assignOwner: "Assign Owner",
        deleteLead: "Delete Lead",
      },
      editDialog: {
        title: "Edit Lead",
        description: "Update the lead information below",
      },
      assignDialog: {
        title: "Assign Owner",
        description: "Select a team member to assign this lead to",
        selectOwner: "Owner",
        selectOwnerPlaceholder: "Select an owner...",
      },
      deleteDialog: {
        title: "Delete Lead",
        description: "Are you sure you want to delete this lead? This action cannot be undone.",
      },
      toasts: {
        leadUpdated: "Lead Updated",
        leadUpdatedDesc: "The lead information has been updated successfully",
        ownerAssigned: "Owner Assigned",
        ownerAssignedDesc: "Lead has been assigned to",
        leadDeleted: "Lead Deleted",
        leadDeletedDesc: "The lead has been deleted successfully",
      },
    },

    // Comprehensive translations for new lead form
    leadsForm: {
      title: "Leads",
      subtitle: "Manage and track your sales leads",
      newLead: "New Lead",
      newLeadDescription: "Create a new lead in your pipeline",
      export: "Export",
      searchPlaceholder: "Search leads...",
      allStages: "All Stages",
      contactInformation: "Contact Information",
      name: "Name",
      namePlaceholder: "Enter lead's full name",
      email: "Email",
      emailPlaceholder: "email@example.com",
      phone: "Phone",
      phonePlaceholder: "+1 (555) 000-0000",
      source: "Source",
      campaign: "Campaign",
      campaignPlaceholder: "Summer Sale 2024",
      keyword: "Keyword",
      keywordPlaceholder: "luxury apartments mexico city",
      sources: {
        website: "Website",
        referral: "Referral",
        direct: "Direct",
      },
      tableHeaders: {
        lead: "Lead",
        attribution: "Attribution",
        stage: "Stage",
        owner: "Owner",
      },
    },

    // Common translations
    common: {
      save: "Save",
      cancel: "Cancel",
      saving: "Saving...",
      name: "Name",
      email: "Email",
      phone: "Phone",
      notes: "Notes",
      assign: "Assign",
      delete: "Delete",
    },

    // Inbox translations
    inboxPage: {
      title: "Bandeja Multicanal",
      subtitle: "Unified conversations across all channels",
      all: "All",
      whatsapp: "WhatsApp",
      web: "Web",
      instagram: "Instagram",
      facebook: "Facebook",
      email: "Email",
      assignedTo: "Assigned to",
      unassigned: "Unassigned",
      assignButton: "Assign",
      viewLeadButton: "View Lead",
      aiSummary: "AI Summary (Amazon Bedrock Ready)",
      nextBestAction: "Next Best Action:",
      typeMessage: "Type your message...",
      pressEnter: "Press Enter to send",
      botMode: "Bot mode:",
      active: "Active",
      inactive: "Inactive",
      lead: "lead",
      bot: "bot",
      agent: "agent",
      assignDialog: {
        title: "Assign Conversation",
        description: "Select a team member to assign this conversation to",
        selectAgent: "Agent",
        selectAgentPlaceholder: "Select an agent...",
      },
      toasts: {
        conversationAssigned: "Conversation Assigned",
        conversationAssignedDesc: "Conversation has been assigned to",
      },
    },

    // Lead Detail Page translations
    leadDetail: {
      notFound: "Lead not found",
      notFoundDesc: "The lead with ID does not exist",
      edit: "Edit",
      sendMessage: "Send Message",
      contactInformation: "Contact Information",
      leadOwner: "Lead Owner",
      noOwnerAssigned: "No owner assigned",
      leadScore: "Lead Score",
      hot: "Hot",
      warm: "Warm",
      cold: "Cold",
      aiPoweredScoring: "AI-powered lead scoring (Amazon Bedrock ready)",
      attribution: "Attribution",
      conversations: "Conversations",
      notes: "Notes & Tasks",
      timeline: "Timeline",
      bot: "Bot",
      human: "Human",
      aiSummary: "AI Summary",
      nextBestAction: "Next Best Action",
      noConversations: "No conversations yet",
      noNotes: "No notes or tasks",
      addNote: "Add Note",
      activityTimeline: "Activity Timeline",
      noActivity: "No activity yet",
      by: "by",
      created: "Created",
      lastContacted: "Last contacted",
      addNoteDialog: {
        title: "Add Note",
        description: "Add a note or task for this lead",
        notePlaceholder: "Enter your note here...",
        isTask: "This is a task",
        dueDate: "Due date",
      },
      toasts: {
        noteAdded: "Note Added",
        noteAddedDesc: "The note has been added successfully",
      },
    },

    // Properties page translations
    propertiesPage: {
      title: "Property Inventory",
      subtitle: "Manage your property catalog and availability",
      addProperty: "Add Property",
      searchPlaceholder: "Search properties...",
      allStatus: "All Status",
      filterByStatus: "Filter by status",
      statusLabels: {
        available: "Available",
        reserved: "Reserved",
        sold: "Sold",
        rented: "Rented",
      },
      propertyTypes: {
        apartment: "Apartment",
        house: "House",
        commercial: "Commercial",
      },
    },

    // Calendar page translations
    calendarPage: {
      title: "Calendar",
      subtitle: "Manage appointments and schedule viewings",
      newEvent: "New Event",
      today: "Today",
      week: "Week",
      month: "Month",
      eventTypes: {
        viewing: "Viewing",
        meeting: "Meeting",
        call: "Call",
        follow_up: "Follow-up",
        other: "Other",
      },
      eventStatus: {
        scheduled: "Scheduled",
        completed: "Completed",
        cancelled: "Cancelled",
        no_show: "No Show",
      },
      lead: "Lead",
      location: "Location",
      edit: "Edit",
    },

    // Lead Scoring page translations
    scoringPage: {
      title: "Lead Scoring",
      subtitle: "AI-powered lead qualification and prioritization",
      modelTitle: "AI Scoring Model (Amazon Bedrock Ready)",
      modelDescription: "Machine learning factors used to calculate lead scores",
      factors: {
        budgetMatch: {
          title: "Budget Match",
          weight: "30%",
          description: "Alignment between lead budget and property prices",
        },
        timelineUrgency: {
          title: "Timeline Urgency",
          weight: "25%",
          description: "How soon the lead wants to purchase",
        },
        engagementLevel: {
          title: "Engagement Level",
          weight: "20%",
          description: "Response rate and conversation quality",
        },
        financialReadiness: {
          title: "Financial Readiness",
          weight: "15%",
          description: "Pre-approval status and payment capability",
        },
        profileCompleteness: {
          title: "Profile Completeness",
          weight: "10%",
          description: "Amount of information provided",
        },
        additionalFactors: "Additional factors learned from historical data",
      },
      leadsTitle: "Lead Scores",
      leadsDescription: "Prioritized list of all scored leads",
      recalculateAll: "Recalculate All",
      scoreLabels: {
        hot: "Hot",
        warm: "Warm",
        cold: "Cold",
      },
      unassigned: "Unassigned",
      view: "View",
    },

    // Assignment Rules page translations
    routingPage: {
      title: "Assignment Rules",
      subtitle: "Configure intelligent lead routing and assignment",
      newRule: "New Rule",
      stats: {
        activeRules: "Active Rules",
        activeAgents: "Active Agents",
        avgResponseTime: "Avg Response Time",
      },
      priority: "Priority",
      active: "Active",
      inactive: "Inactive",
      assignmentStrategy: "Assignment Strategy",
      assignTo: "Assign To",
      conditions: "Conditions",
      source: "Source",
      tags: "Tags",
      campaign: "Campaign",
      strategies: {
        round_robin: "Round Robin",
        load_balanced: "Load Balanced",
        specialty_match: "Specialty Match",
        manual: "Manual Assignment",
      },
      strategyDescriptions: {
        round_robin: "Distribute leads evenly in rotation",
        load_balanced: "Assign based on current workload",
        specialty_match: "Match lead requirements with agent specialties",
        manual: "No automatic assignment",
      },
      createRule: {
        title: "Create New Assignment Rule",
        description: "Define conditions and assignment strategy for incoming leads",
        ruleName: "Rule Name",
        ruleNamePlaceholder: "e.g., VIP Leads to Senior Agents",
        strategy: "Assignment Strategy",
        priority: "Priority",
        saveRule: "Save Rule",
        cancel: "Cancel",
      },
    },

    aiAssistantPage: {
      title: "AI Assistant",
      subtitle: "Intelligent insights powered by Amazon Bedrock (UI Ready)",
      stats: {
        hotLeads: "Hot Leads",
        unassigned: "Unassigned",
        botActive: "Bot Active",
        aiInsights: "AI Insights",
      },
      insights: {
        title: "AI-Powered Insights & Recommendations",
        subtitle: "Real-time intelligence from Amazon Bedrock analysis",
        priority: {
          high: "high",
          medium: "medium",
          low: "low",
        },
      },
      bedrock: {
        title: "Amazon Bedrock Integration (Ready)",
        subtitle: "AI capabilities prepared for production deployment",
        features: {
          leadScoring: {
            title: "Lead Scoring",
            description: "Automatic lead qualification and prioritization",
          },
          conversationAnalysis: {
            title: "Conversation Analysis",
            description: "Intent detection and sentiment analysis",
          },
          nextBestAction: {
            title: "Next Best Action",
            description: "AI-recommended follow-up strategies",
          },
          objectionDetection: {
            title: "Objection Detection",
            description: "Identify and address customer concerns",
          },
        },
      },
      sampleInsights: {
        highValueLead: {
          title: "High-Value Lead Detected",
          description: "Ana López shows strong buying signals with budget match and immediate timeline",
          action: "Schedule property viewing within 48 hours",
        },
        followUp: {
          title: "Follow-up Opportunity",
          description: "Roberto Sánchez hasn't been contacted in 3 days, risk of losing engagement",
          action: "Send follow-up WhatsApp with property recommendations",
        },
        priceObjection: {
          title: "Price Objection Detected",
          description: "Multiple leads mentioned budget concerns in conversations this week",
          action: "Consider showcasing financing options or lower-priced properties",
        },
        botPerformance: {
          title: "Bot Performance Alert",
          description: "Chatbot qualification rate is 15% below target this week",
          action: "Review bot conversation flows and update training data",
        },
      },
      lead: "Lead",
    },

    // Quotes page
    createNewQuote: "Create New Quote",
    generatePropertyQuote: "Generate a property quote for a lead",
    selectLead: "Select Lead",
    chooseALead: "Choose a lead...",
    addProperty: "Add property...",
    noPropertiesAdded: "No properties added yet",
    quantity: "Quantity",
    discount: "Discount",
    additionalInformation: "Additional Information",
    notes: "Notes",
    addNotesForClient: "Add any notes for the client...",
    termsAndConditions: "Terms & Conditions",
    paymentTerms: "Payment terms, conditions, etc...",
    quoteSummary: "Quote Summary",
    subtotal: "Subtotal",
    discounts: "Discounts",
    taxes: "Taxes",
    total: "Total",
    currency: "Currency",
    saveAsDraft: "Save as Draft",
    saveAndSend: "Save & Send",
    exportOptions: "Export Options (UI Ready)",
    exportAsPDF: "Export as PDF",
    sendViaWhatsApp: "Send via WhatsApp",
    sendViaEmail: "Send via Email",

    // Pipelines page translations
    pipelinesPage: {
      title: "Sales Pipelines",
      subtitle: "Manage your sales funnels and stages",
      createPipeline: "Create Pipeline",
      stages: "stages",
      activeLeads: "active leads",
      requiredField: "required field",
      requiredFields: "required fields",
      default: "Default",
      stageNames: {
        newLead: "New Lead",
        contacted: "Contacted",
        qualified: "Qualified",
        viewingScheduled: "Viewing Scheduled",
        proposal: "Proposal",
        negotiation: "Negotiation",
        closedWon: "Closed Won",
        closedLost: "Closed Lost",
      },
    },

    // Property Detail Page translations
    propertyDetail: {
      title: "Property Details",
      notFound: "Property not found",
      notFoundDesc: "The property with ID does not exist",
      overview: "Overview",
      location: "Location",
      features: "Features & Amenities",
      contactAgent: "Contact Agent",
      scheduleViewing: "Schedule Viewing",
      shareProperty: "Share Property",
      edit: "Edit",
      price: "Price",
      status: "Status",
      type: "Type",
      bedrooms: "Bedrooms",
      bathrooms: "Bathrooms",
      area: "Area",
      parking: "Parking Spots",
      address: "Address",
      coordinates: "Coordinates",
      description: "Description",
      amenities: "Amenities",
      images: "Images",
      relatedLeads: "Related Leads",
      activityHistory: "Activity History",
      noActivity: "No activity recorded",
      createdOn: "Created on",
      lastUpdated: "Last updated",
      statusLabels: {
        available: "Available",
        reserved: "Reserved",
        sold: "Sold",
        rented: "Rented",
      },
      propertyTypes: {
        apartment: "Apartment",
        house: "House",
        condo: "Condo",
        land: "Land",
        commercial: "Commercial",
      },
    },

    // Navigation groups
    navGroup: {
      communication: "Communication",
      ai: "AI",
      medicalOps: "Medical Operations",
      admin: "Administration",
    },

    // New nav items
    unifiedInbox: "Unified Inbox",
    patients: "Patients",
    physiotherapists: "Physiotherapists",
    appointments: "Appointments",
    automations: "Automations",
    aiConfig: "AI Configuration",
    aiMonitoring: "AI Monitoring",
    debugReplay: "Debug / Replay",
    operationalLogs: "Operational Logs",
    tenantManagement: "Clinic Management",
    birthdayNotifications: "Birthday Notifications",

    // Inbox tabs
    inboxTab: {
      all: "All",
      attention: "Needs Attention",
      inProgress: "In Progress",
      closed: "Closed",
    },

    // Quick actions
    quickAction: {
      newConversation: "+ New Conversation",
      scheduleAppointment: "+ Schedule Appointment",
    },

    // Favorites section
    favorites: {
      title: "Favorites",
      add: "Add to favorites",
      remove: "Remove from favorites",
    },

    // Patients module
    patientsModule: {
      title: "Patients",
      searchPlaceholder: "Search patients by name or phone...",
      columns: {
        name: "Name",
        phone: "Phone",
        lastConsult: "Last Consultation",
        recordStatus: "Record Status",
      },
      recordStatusLabels: {
        complete: "Complete",
        partial: "Partial",
        empty: "Empty",
      },
      detail: {
        age: "Age",
        birthDate: "Date of Birth",
        weight: "Weight",
        height: "Height",
        consultReason: "Reason for Consultation",
        painLevel: "Pain Level",
        medicalHistory: "Medical History",
        surgicalHistory: "Surgical History",
        traumaticHistory: "Traumatic History",
        conversations: "Associated Conversations",
        editRecord: "Edit Record",
        deletePatient: "Delete Patient",
      },
      phiToggle: "Show sensitive data (PHI)",
      newPatient: "New Patient",
      emptyState: "No patients found",
      errorLoading: "Error loading patients",
      form: {
        fields: {
          name: "Name",
          phone: "Phone",
          birthDate: "Date of Birth",
          age: "Age",
          weight: "Weight (kg)",
          height: "Height (cm)",
          consultReason: "Reason for Consultation",
          painLevel: "Pain Level (0-10)",
          medicalHistory: "Medical History",
          surgicalHistory: "Surgical History",
          traumaticHistory: "Traumatic History",
        },
        placeholders: {
          name: "Patient full name",
          phone: "+52 55 1234 5678",
          age: "Years",
          weight: "kg",
          height: "cm",
          consultReason: "Describe the reason for consultation...",
          medicalHistory: "Relevant medical history...",
          surgicalHistory: "Previous surgeries...",
          traumaticHistory: "Previous injuries or trauma...",
        },
        buttons: {
          create: "Create Patient",
          update: "Save Changes",
        },
        validation: {
          required: "This field is required",
          phoneMinDigits: "Phone must have at least 10 digits",
          birthDateFuture: "Date of birth cannot be in the future",
          positiveNumber: "Must be a positive number",
          painLevelRange: "Must be an integer between 0 and 10",
        },
        errors: {
          duplicatePhone: "A patient with this phone number already exists",
          submitError: "Error saving patient",
        },
      },
      deleteDialog: {
        title: "Delete Patient",
        description: "Are you sure you want to delete {{name}}? The patient will be removed from the list but the record will be preserved.",
        confirm: "Delete",
        cancel: "Cancel",
        deleting: "Deleting...",
      },
    },

    // Appointments module
    appointmentsModule: {
      title: "Appointments",
      searchPlaceholder: "Search by patient name or phone...",
      columns: {
        patient: "Patient",
        dateTime: "Date & Time",
        type: "Type",
        status: "Status",
        doctor: "Doctor",
      },
      statusTabs: {
        upcoming: "Upcoming",
        cancelled: "Cancelled",
        rescheduled: "Rescheduled",
        noShow: "No Show",
        completed: "Completed",
      },
      dateTo: "To",
      dateRangeFilter: "Filter by date range",
      detailPanel: {
        title: "Appointment Details",
        notes: "Notes",
        duration: "Duration",
        location: "Location",
        linkedConversation: "Linked Conversation",
      },
      newAppointment: "New Appointment",
      editAppointment: "Edit",
      form: {
        fields: {
          patientName: "Patient Name",
          patientPhone: "Patient Phone",
          dateTime: "Date & Time",
          type: "Type",
          doctor: "Doctor",
          notes: "Notes",
          duration: "Duration (min)",
          location: "Location",
        },
        placeholders: {
          patientName: "Patient full name",
          patientPhone: "+52 55 1234 5678",
          type: "e.g. Consultation, Follow-up",
          doctor: "Doctor name",
          notes: "Additional notes...",
          duration: "Minutes",
          location: "Office or address",
        },
        buttons: {
          create: "Create Appointment",
          update: "Save Changes",
        },
        validation: {
          required: "This field is required",
          positiveNumber: "Must be a positive number",
        },
        errors: {
          submitError: "Error saving appointment",
        },
      },
      emptyState: "No appointments found",
      errorLoading: "Error loading appointments",
    },

    // Automations module
    automationsModule: {
      title: "Automations",
      columns: {
        name: "Name",
        type: "Type",
        status: "Status",
        lastExecution: "Last Execution",
      },
      form: {
        title: "Automation Configuration",
        name: "Name",
        typeSelector: {
          label: "Automation Type",
          scheduledMessage: "Scheduled Message",
          reminder: "Reminder",
          eventTrigger: "Event Trigger",
        },
        triggerEvent: "Trigger Event",
        messageTemplate: "Message Template",
        templateVariables: "Available Variables",
        schedule: "Schedule (cron)",
      },
      executionHistory: {
        title: "Execution History",
        columns: {
          date: "Date",
          recipient: "Recipient",
          deliveryStatus: "Delivery Status",
          error: "Error",
        },
        statusLabels: {
          delivered: "Delivered",
          failed: "Failed",
          pending: "Pending",
        },
      },
      toggle: "Active",
      createNew: "New Automation",
      editAutomation: "Edit Automation",
      backToList: "Back to list",
      triggerEvents: {
        appointmentScheduled: "Appointment Scheduled",
        appointmentCancelled: "Appointment Cancelled",
        intakeCompleted: "Intake Completed",
        appointmentRescheduled: "Appointment Rescheduled",
        noShow: "No-Show Detected",
      },
      templateVariablesHint: "Use {{variable}} to insert dynamic values",
      templateVariablesList: {
        patientName: "Patient Name",
        appointmentDate: "Appointment Date",
        doctorName: "Doctor Name",
      },
      typeLabels: {
        scheduled_message: "Scheduled Message",
        reminder: "Reminder",
        event_trigger: "Event Trigger",
      },
      statusLabels: {
        active: "Active",
        inactive: "Inactive",
      },
      emptyState: "No automations configured",
      errorLoading: "Error loading automations",
      errorSaving: "Error saving automation",
    },

    // AI Config module
    aiConfigModule: {
      title: "AI Configuration",
      sections: {
        prompts: "Prompts",
        intents: "Intents",
        toneConfig: "Conversation Tone",
      },
      prompts: {
        name: "Prompt Name",
        version: "Version",
        lastModified: "Last Modified",
        status: "Status",
        statusLabels: {
          active: "Active",
          draft: "Draft",
        },
        content: "Content",
        contentPlaceholder: "Enter the prompt content for the new version...",
        createVersion: "Create New Version",
        createVersionDescription: "Write the content for the new version of this prompt.",
        activateVersion: "Activate Version",
        activate: "Activate",
        emptyState: "No prompts configured",
        cancel: "Cancel",
        create: "Create",
      },
      intents: {
        toggle: "Enable Intent",
        disableConfirmation: "Disabling this intent will affect the AI behavior. Are you sure?",
        impactDescription: "Impact",
        name: "Intent Name",
        description: "Description",
        confirmDisable: "Confirm",
        emptyState: "No intents configured",
      },
      tone: {
        presets: {
          formal: "Formal",
          friendly: "Friendly",
          neutral: "Neutral",
          custom: "Custom",
        },
        customInstructions: "Custom Instructions",
        customInstructionsPlaceholder: "Enter custom tone instructions...",
        selectPreset: "Select tone preset",
        saved: "Tone configuration saved",
      },
      errorLoading: "Error loading AI configuration",
      errorSaving: "Error saving AI configuration",
    },

    // AI Monitoring module
    aiMonitoringModule: {
      title: "AI Monitoring",
      metrics: {
        fallbackRate: "Fallback Rate",
        humanInterventionRate: "Human Intervention Rate",
        averageConfidence: "Average Confidence",
      },
      periodSelector: {
        today: "Today",
        "7days": "Last 7 Days",
        "30days": "Last 30 Days",
        custom: "Custom Range",
      },
      trendChart: {
        fallback: "Fallback Trend",
        interventions: "Interventions Trend",
        confidence: "Confidence Trend",
      },
      lowConfidenceTable: {
        title: "Low Confidence Conversations",
        columns: {
          conversation: "Conversation",
          phone: "Phone",
          avgConfidence: "Avg. Confidence",
          messages: "Messages",
          lastActivity: "Last Activity",
        },
        viewReplay: "View Replay",
      },
      emptyState: "No monitoring data available",
      errorLoading: "Error loading AI metrics",
    },

    // Operational Logs module
    operationalLogsModule: {
      title: "Operational Logs",
      columns: {
        timestamp: "Timestamp",
        type: "Type",
        severity: "Severity",
        description: "Description",
        entity: "Entity",
        resolution: "Resolution",
      },
      eventTypes: {
        messageFailure: "Message Failure",
        dlq: "Dead Letter Queue",
        retry: "Retry",
        integrationError: "Integration Error",
        patientCreated: "Patient Created",
        patientUpdated: "Patient Updated",
        patientDeleted: "Patient Deleted",
        appointmentCreated: "Appointment Created",
        appointmentUpdated: "Appointment Updated",
        appointmentDeleted: "Appointment Deleted",
        leadCreated: "Lead Created",
        leadUpdated: "Lead Updated",
        leadDeleted: "Lead Deleted",
        leadAssigned: "Lead Assigned",
        leadStageChanged: "Lead Stage Changed",
        userLogin: "User Login",
        userLogout: "User Logout",
        userCreated: "User Created",
        userUpdated: "User Updated",
        userDeleted: "User Deleted",
        templateCreated: "Template Created",
        templateUpdated: "Template Updated",
        templateDeleted: "Template Deleted",
        automationCreated: "Automation Created",
        automationUpdated: "Automation Updated",
        automationToggled: "Automation Toggled",
        automationDeleted: "Automation Deleted",
        propertyCreated: "Property Created",
        propertyUpdated: "Property Updated",
        propertyDeleted: "Property Deleted",
        visitCreated: "Visit Created",
        visitUpdated: "Visit Updated",
        visitDeleted: "Visit Deleted",
        conversationCreated: "Conversation Created",
        conversationUpdated: "Conversation Updated",
        conversationDeleted: "Conversation Deleted",
        messageSent: "Message Sent",
        roleCreated: "Role Created",
        roleUpdated: "Role Updated",
        roleDeleted: "Role Deleted",
        permissionUpdated: "Permissions Updated",
        tenantCreated: "Tenant Created",
        tenantUpdated: "Tenant Updated",
        tenantDeleted: "Tenant Deleted",
        settingsChanged: "Settings Changed",
        aiConfigUpdated: "AI Config Updated",
        scheduleCreated: "Schedule Created",
        scheduleUpdated: "Schedule Updated",
        scheduleDeleted: "Schedule Deleted",
        widgetConfigUpdated: "Widget Config Updated",
        workdayConfigUpdated: "Workday Config Updated",
        systemAction: "System Action",
      },
      severityLevels: {
        info: "Info",
        warning: "Warning",
        error: "Error",
      },
      filters: {
        byType: "Filter by Type",
        bySeverity: "Filter by Severity",
        byDateRange: "Filter by Date Range",
      },
      detailPanel: {
        title: "Event Details",
        payload: "Details",
        stackTrace: "Stack Trace",
        metadata: "Additional Information",
        technicalId: "Technical ID",
      },
      entityTypes: {
        patient: "Patient",
        appointment: "Appointment",
        schedule: "Schedule",
        lead: "Lead",
        user: "User",
        template: "Template",
        automation: "Automation",
        property: "Property",
        visit: "Visit",
        conversation: "Conversation",
        message: "Message",
        role: "Role",
        permission: "Permission",
        tenant: "Clinic",
        settings: "Settings",
        ai_config: "AI Configuration",
        workday_config: "Work Schedule",
        widget_config: "Widget",
        system: "System",
        unknown: "Record",
      },
      payloadLabels: {
        name: "Name",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email",
        phone: "Phone",
        date: "Date",
        time: "Time",
        startTime: "Start Time",
        endTime: "End Time",
        status: "Status",
        patientName: "Patient Name",
        patientId: "Patient",
        doctorName: "Doctor",
        reason: "Reason",
        notes: "Notes",
        description: "Description",
        type: "Type",
        title: "Title",
        active: "Active",
        enabled: "Enabled",
        dayOfWeek: "Day of Week",
        duration: "Duration",
        location: "Location",
        specialty: "Specialty",
        role: "Role",
      },
      resolved: "Resolved",
      unresolved: "Unresolved",
      emptyState: "No operational events found",
      errorLoading: "Error loading operational logs",
    },

    // Tenant Management module
    tenantManagementModule: {
      title: "Clinic Management",
      columns: {
        clinicName: "Clinic Name",
        status: "Status",
        plan: "Plan",
        createdDate: "Created Date",
      },
      statusLabels: {
        active: "Active",
        inactive: "Inactive",
      },
      configSections: {
        generalInfo: "General Information",
        operationalConfig: "Operational Configuration",
        integrations: "Integrations",
      },
      generalInfo: {
        name: "Clinic Name",
        logo: "Logo",
        primaryColor: "Primary Color",
        secondaryColor: "Secondary Color",
      },
      operationalConfig: {
        businessHours: "Business Hours",
        timezone: "Timezone",
        language: "Language",
      },
      integrations: {
        whatsapp: "WhatsApp",
        whatsappPhone: "WhatsApp Phone Number",
        email: "Email",
        emailSender: "Sender Email Address",
        enabled: "Enabled",
        disabled: "Disabled",
      },
      brandingPreview: "Branding Preview",
      toggle: "Confirm",
      toggleConfirmation: "Are you sure you want to change the clinic status?",
      emptyState: "No clinics found",
      errorLoading: "Error loading clinics",
      errorSaving: "Error saving clinic configuration",
    },

    // Search categories
    searchCategories: {
      conversations: "Conversations",
      patients: "Patients",
      appointments: "Appointments",
      navigation: "Navigation",
      emptyState: "No results found for your search",
    },

    // Replay/Debug panel
    replayPanel: {
      title: "Debug / Replay",
      searchPlaceholder: "Search by conversation ID or phone...",
      recentConversations: "Recent Conversations",
      noConversationSelected: "Select a conversation to start debugging",
      noRecentConversations: "No recent conversations available",
      noSearchResults: "No conversations match your search",
      conversationId: "ID",
      phone: "Phone",
      lastActivity: "Last Activity",
    },

    // Common error/empty states
    errorLoading: "Error loading data",
    retry: "Retry",
    noResults: "No results found",
    loading: "Loading...",
  },
  es: {
    // Navigation & App
    dashboard: "Panel",
    leads: "Leads",
    contacts: "Contactos",
    pipelines: "Embudos",
    inbox: "Bandeja",
    properties: "Propiedades",
    quotes: "Cotizaciones",
    calendar: "Calendario",
    scheduleManagement: "Gestión de Horarios",
    routing: "Asignación",
    scoring: "Lead Scoring",
    aiAssistant: "Asistente IA",
    aiPrequalification: "Asesor Virtual IA",
    aiRecommendations: "Recomendaciones IA",
    auditLog: "Registro de Auditoría",
    settings: "Configuración",
    logout: "Cerrar Sesión",
    reports: "Reportes",
    chatWidget: "Widget de Chat",
    help: "Ayuda",
    visits: "Visitas",
    roles: "Roles",
    permissions: "Permisos",
    templates: "Plantillas",
    users: "Usuarios",
    medicalConversations: "Conversaciones Médicas",
    postOpFollowup: "Seguimiento Post-Op",
  
    // Reports & Analytics
    reportsPage: {
      title: "Reportes y Analytics",
      subtitle: "Visualización avanzada preparada para Amazon QuickSight",
    },

    // Common
    search: "Buscar",
    filter: "Filtrar",
    export: "Exportar",
    import: "Importar",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    create: "Crear",
    view: "Ver",
    actions: "Acciones",
    status: "Estado",
    createdAt: "Creado",
    updatedAt: "Actualizado",
    saving: "Guardando...",

    // Lead specific
    newLead: "Nuevo Lead",
    leadSource: "Fuente",
    leadStage: "Etapa",
    leadOwner: "Responsable",
    leadScore: "Puntuación",
    attribution: "Atribución",
    campaign: "Campaña",

    // Property specific
    available: "Disponible",
    sold: "Vendida",
    reserved: "Reservada",
    rented: "Rentada",

    // Phase labels
    phase1: "Fase 1",
    phase2: "Fase 2",
    phase3: "Fase 3",

    // Comprehensive lead page translations
    leadsPage: {
      title: "Leads",
      subtitle: "Gestiona y rastrea tus pacientes",
      searchPlaceholder: "Buscar leads...",
      allStages: "Todas las Etapas",
      filterByStage: "Filtrar por etapa",
      tableHeaders: {
        lead: "Lead",
        attribution: "Atribución",
        stage: "Etapa",
        owner: "Responsable",
        score: "Puntuación",
        created: "Creado",
        actions: "Acciones",
      },
      unassigned: "Sin asignar",
      dropdownActions: {
        viewDetails: "Ver Detalles",
        editLead: "Editar Lead",
        assignOwner: "Asignar Responsable",
        deleteLead: "Eliminar Lead",
      },
      editDialog: {
        title: "Editar Lead",
        description: "Actualiza la información del lead a continuación",
      },
      assignDialog: {
        title: "Asignar Responsable",
        description: "Selecciona un miembro del equipo para asignar este lead",
        selectOwner: "Responsable",
        selectOwnerPlaceholder: "Selecciona un responsable...",
      },
      deleteDialog: {
        title: "Eliminar Lead",
        description: "¿Estás seguro de que quieres eliminar este lead? Esta acción no se puede deshacer.",
      },
      toasts: {
        leadUpdated: "Lead Actualizado",
        leadUpdatedDesc: "La información del lead ha sido actualizada exitosamente",
        ownerAssigned: "Responsable Asignado",
        ownerAssignedDesc: "El lead ha sido asignado a",
        leadDeleted: "Lead Eliminado",
        leadDeletedDesc: "El lead ha sido eliminado exitosamente",
      },
    },

    leadsForm: {
      title: "Leads",
      subtitle: "Gestiona y rastrea tus pacientes",
      newLead: "Nuevo Lead",
      newLeadDescription: "Crear un nuevo lead en tu pipeline",
      export: "Exportar",
      searchPlaceholder: "Buscar leads...",
      allStages: "Todas las Etapas",
      contactInformation: "Información de Contacto",
      name: "Nombre",
      namePlaceholder: "Ingresa el nombre completo del lead",
      email: "Email",
      emailPlaceholder: "email@ejemplo.com",
      phone: "Teléfono",
      phonePlaceholder: "+52 (55) 0000-0000",
      source: "Fuente",
      campaign: "Campaña",
      campaignPlaceholder: "Venta de Verano 2024",
      keyword: "Palabra Clave",
      keywordPlaceholder: "departamentos de lujo ciudad de méxico",
      sources: {
        website: "Sitio Web",
        referral: "Referido",
        direct: "Directo",
      },
      tableHeaders: {
        lead: "Lead",
        attribution: "Atribución",
        stage: "Etapa",
        owner: "Responsable",
      },
    },

    common: {
      save: "Guardar",
      cancel: "Cancelar",
      saving: "Guardando...",
      name: "Nombre",
      email: "Email",
      phone: "Teléfono",
      notes: "Notas",
      assign: "Asignar",
      delete: "Eliminar",
    },

    inboxPage: {
      title: "Bandeja Omnicanal",
      subtitle: "Conversaciones unificadas en todos los canales",
      all: "Todos",
      whatsapp: "WhatsApp",
      web: "Web",
      instagram: "Instagram",
      facebook: "Facebook",
      email: "Email",
      assignedTo: "Asignado a",
      unassigned: "Sin asignar",
      assignButton: "Asignar",
      viewLeadButton: "Ver Lead",
      aiSummary: "Resumen IA (Amazon Bedrock Listo)",
      nextBestAction: "Próxima Mejor Acción:",
      typeMessage: "Escribe tu mensaje...",
      pressEnter: "Presiona Enter para enviar",
      botMode: "Modo bot:",
      active: "Activo",
      inactive: "Inactivo",
      lead: "lead",
      bot: "bot",
      agent: "agente",
      assignDialog: {
        title: "Asignar Conversación",
        description: "Selecciona un miembro del equipo para asignar esta conversación",
        selectAgent: "Agente",
        selectAgentPlaceholder: "Selecciona un agente...",
      },
      toasts: {
        conversationAssigned: "Conversación Asignada",
        conversationAssignedDesc: "La conversación ha sido asignada a",
      },
    },

    leadDetail: {
      notFound: "Lead no encontrado",
      notFoundDesc: "El lead con ID no existe",
      edit: "Editar",
      sendMessage: "Enviar Mensaje",
      contactInformation: "Información de Contacto",
      leadOwner: "Responsable del Lead",
      noOwnerAssigned: "Sin responsable asignado",
      leadScore: "Puntuación del Lead",
      hot: "Caliente",
      warm: "Tibio",
      cold: "Frío",
      aiPoweredScoring: "Puntuación de leads con IA (Amazon Bedrock listo)",
      attribution: "Atribución",
      conversations: "Conversaciones",
      notes: "Notas y Tareas",
      timeline: "Línea de Tiempo",
      bot: "Bot",
      human: "Humano",
      aiSummary: "Resumen IA",
      nextBestAction: "Próxima Mejor Acción",
      noConversations: "Sin conversaciones aún",
      noNotes: "Sin notas o tareas",
      addNote: "Agregar Nota",
      activityTimeline: "Línea de Tiempo de Actividad",
      noActivity: "Sin actividad aún",
      by: "por",
      created: "Creado",
      lastContacted: "Último contacto",
      addNoteDialog: {
        title: "Agregar Nota",
        description: "Agrega una nota o tarea para este lead",
        notePlaceholder: "Ingresa tu nota aquí...",
        isTask: "Esto es una tarea",
        dueDate: "Fecha de vencimiento",
      },
      toasts: {
        noteAdded: "Nota Agregada",
        noteAddedDesc: "La nota ha sido agregada exitosamente",
      },
    },

    // Properties page translations in Spanish
    propertiesPage: {
      title: "Inventario de Propiedades",
      subtitle: "Gestiona tu catálogo de propiedades y disponibilidad",
      addProperty: "Agregar Propiedad",
      searchPlaceholder: "Buscar propiedades...",
      allStatus: "Todos los Estados",
      filterByStatus: "Filtrar por estado",
      statusLabels: {
        available: "Disponible",
        reserved: "Reservado",
        sold: "Vendido",
        rented: "Rentado",
      },
      propertyTypes: {
        apartment: "Departamento",
        house: "Casa",
        commercial: "Comercial",
      },
    },

    // Calendar page translations in Spanish
    calendarPage: {
      title: "Calendario",
      subtitle: "Gestiona citas y programa visitas",
      newEvent: "Nuevo Evento",
      today: "Hoy",
      week: "Semana",
      month: "Mes",
      eventTypes: {
        viewing: "Visita",
        meeting: "Reunión",
        call: "Llamada",
        follow_up: "Seguimiento",
        other: "Otro",
      },
      eventStatus: {
        scheduled: "Programado",
        completed: "Completado",
        cancelled: "Cancelado",
        no_show: "No Asistió",
      },
      lead: "Lead",
      location: "Ubicación",
      edit: "Editar",
    },

    // Lead Scoring page translations in Spanish
    scoringPage: {
      title: "Puntuación de Leads",
      subtitle: "Calificación y priorización de leads impulsada por IA",
      modelTitle: "Modelo de Puntuación IA (Amazon Bedrock Listo)",
      modelDescription: "Factores de aprendizaje automático usados para calcular puntuaciones de leads",
      factors: {
        budgetMatch: {
          title: "Coincidencia de Presupuesto",
          weight: "30%",
          description: "Alineación entre presupuesto del lead y precios de propiedades",
        },
        timelineUrgency: {
          title: "Urgencia de Cronograma",
          weight: "25%",
          description: "Qué tan pronto el lead quiere comprar",
        },
        engagementLevel: {
          title: "Nivel de Engagement",
          weight: "20%",
          description: "Tasa de respuesta y calidad de conversación",
        },
        financialReadiness: {
          title: "Preparación Financiera",
          weight: "15%",
          description: "Estado de pre-aprobación y capacidad de pago",
        },
        profileCompleteness: {
          title: "Completitud del Perfil",
          weight: "10%",
          description: "Cantidad de información proporcionada",
        },
        additionalFactors: "Factores adicionales aprendidos de datos históricos",
      },
      leadsTitle: "Puntuaciones de Leads",
      leadsDescription: "Lista priorizada de todos los leads puntuados",
      recalculateAll: "Recalcular Todos",
      scoreLabels: {
        hot: "Caliente",
        warm: "Tibio",
        cold: "Frío",
      },
      unassigned: "Sin asignar",
      view: "Ver",
    },

    // Assignment Rules page translations in Spanish
    routingPage: {
      title: "Reglas de Asignación",
      subtitle: "Configura el enrutamiento y asignación inteligente de leads",
      newRule: "Nueva Regla",
      stats: {
        activeRules: "Reglas Activas",
        activeAgents: "Agentes Activos",
        avgResponseTime: "Tiempo Promedio de Respuesta",
      },
      priority: "Prioridad",
      active: "Activa",
      inactive: "Inactiva",
      assignmentStrategy: "Estrategia de Asignación",
      assignTo: "Asignar A",
      conditions: "Condiciones",
      source: "Fuente",
      tags: "Etiquetas",
      campaign: "Campaña",
      strategies: {
        round_robin: "Round Robin",
        load_balanced: "Balanceo de Carga",
        specialty_match: "Coincidencia de Especialidad",
        manual: "Asignación Manual",
      },
      strategyDescriptions: {
        round_robin: "Distribuir leads equitativamente en rotación",
        load_balanced: "Asignar basado en carga de trabajo actual",
        specialty_match: "Coincidir requisitos del lead con especialidades del agente",
        manual: "Sin asignación automática",
      },
      createRule: {
        title: "Crear Nueva Regla de Asignación",
        description: "Define condiciones y estrategia de asignación para leads entrantes",
        ruleName: "Nombre de la Regla",
        ruleNamePlaceholder: "ej., Leads VIP a Agentes Senior",
        strategy: "Estrategia de Asignación",
        priority: "Prioridad",
        saveRule: "Guardar Regla",
        cancel: "Cancelar",
      },
    },

    aiAssistantPage: {
      title: "Asistente IA",
      subtitle: "Insights inteligentes impulsados por Amazon Bedrock (UI Listo)",
      stats: {
        hotLeads: "Leads Calientes",
        unassigned: "Sin asignar",
        botActive: "Bot Activo",
        aiInsights: "Insights IA",
      },
      insights: {
        title: "Insights y Recomendaciones Impulsados por IA",
        subtitle: "Inteligencia en tiempo real desde análisis de Amazon Bedrock",
        priority: {
          high: "alta",
          medium: "media",
          low: "baja",
        },
      },
      bedrock: {
        title: "Integración Amazon Bedrock (Listo)",
        subtitle: "Capacidades de IA preparadas para despliegue en producción",
        features: {
          leadScoring: {
            title: "Puntuación de Leads",
            description: "Calificación y priorización automática de leads",
          },
          conversationAnalysis: {
            title: "Análisis de Conversaciones",
            description: "Detección de intención y análisis de sentimiento",
          },
          nextBestAction: {
            title: "Próxima Mejor Acción",
            description: "Estrategias de seguimiento recomendadas por IA",
          },
          objectionDetection: {
            title: "Detección de Objeciones",
            description: "Identificar y abordar preocupaciones del cliente",
          },
        },
      },
      sampleInsights: {
        highValueLead: {
          title: "Lead de Alto Valor Detectado",
          description: "Ana López muestra fuertes señales de compra con presupuesto coincidente y cronograma inmediato",
          action: "Programar visita a propiedad dentro de 48 horas",
        },
        followUp: {
          title: "Oportunidad de Seguimiento",
          description: "Roberto Sánchez no ha sido contactado en 3 días, riesgo de perder engagement",
          action: "Enviar seguimiento por WhatsApp con recomendaciones de propiedades",
        },
        priceObjection: {
          title: "Objeción de Precio Detectada",
          description: "Múltiples leads mencionaron preocupaciones de presupuesto en conversaciones esta semana",
          action: "Considerar mostrar opciones de financiamiento o propiedades de menor precio",
        },
        botPerformance: {
          title: "Alerta de Rendimiento del Bot",
          description: "Tasa de calificación del chatbot está 15% por debajo del objetivo esta semana",
          action: "Revisar flujos de conversación del bot y actualizar datos de entrenamiento",
        },
      },
      lead: "Lead",
    },

    // Quotes page
    createNewQuote: "Crear Nueva Cotización",
    generatePropertyQuote: "Generar cotización de propiedad para un lead",
    selectLead: "Seleccionar Lead",
    chooseALead: "Elige un lead...",
    addProperty: "Agregar propiedad...",
    noPropertiesAdded: "No se han agregado propiedades",
    quantity: "Cantidad",
    discount: "Descuento",
    additionalInformation: "Información Adicional",
    notes: "Notas",
    addNotesForClient: "Agrega notas para el cliente...",
    termsAndConditions: "Términos y Condiciones",
    paymentTerms: "Términos de pago, condiciones, etc...",
    quoteSummary: "Resumen de Cotización",
    subtotal: "Subtotal",
    discounts: "Descuentos",
    taxes: "Impuestos",
    total: "Total",
    currency: "Moneda",
    saveAsDraft: "Guardar como Borrador",
    saveAndSend: "Guardar y Enviar",
    exportOptions: "Opciones de Exportación (UI Ready)",
    exportAsPDF: "Exportar como PDF",
    sendViaWhatsApp: "Enviar por WhatsApp",
    sendViaEmail: "Enviar por Email",

    // Pipelines page translations in Spanish
    pipelinesPage: {
      title: "Embudos de Atención",
      subtitle: "Gestiona tus embudos y etapas de atención",
      createPipeline: "Crear Embudo",
      stages: "etapas",
      activeLeads: "leads activos",
      requiredField: "campo requerido",
      requiredFields: "campos requeridos",
      default: "Predeterminado",
      stageNames: {
        newLead: "Nuevo Lead",
        contacted: "Contactado",
        qualified: "Calificado",
        viewingScheduled: "Visita Agendada",
        proposal: "Propuesta",
        negotiation: "Negociación",
        closedWon: "Cerrado Ganado",
        closedLost: "Cerrado Perdido",
      },
    },

    // Property Detail Page translations in Spanish
    propertyDetail: {
      title: "Detalles de la Propiedad",
      notFound: "Propiedad no encontrada",
      notFoundDesc: "La propiedad con ID no existe",
      overview: "Resumen",
      location: "Ubicación",
      features: "Características y Amenidades",
      contactAgent: "Contactar Agente",
      scheduleViewing: "Agendar Visita",
      shareProperty: "Compartir Propiedad",
      edit: "Editar",
      price: "Precio",
      status: "Estado",
      type: "Tipo",
      bedrooms: "Recámaras",
      bathrooms: "Baños",
      area: "Área",
      parking: "Lugares de Estacionamiento",
      address: "Dirección",
      coordinates: "Coordenadas",
      description: "Descripción",
      amenities: "Amenidades",
      images: "Imágenes",
      relatedLeads: "Leads Relacionados",
      activityHistory: "Historial de Actividad",
      noActivity: "No hay actividad registrada",
      createdOn: "Creado el",
      lastUpdated: "Última actualización",
      statusLabels: {
        available: "Disponible",
        reserved: "Reservado",
        sold: "Vendido",
        rented: "Rentado",
      },
      propertyTypes: {
        apartment: "Departamento",
        house: "Casa",
        condo: "Condominio",
        land: "Terreno",
        commercial: "Comercial",
      },
    },

    // Navigation groups
    navGroup: {
      communication: "Comunicación",
      ai: "IA",
      medicalOps: "Operación Médica",
      admin: "Administración",
    },

    // New nav items
    unifiedInbox: "Bandeja Unificada",
    patients: "Pacientes",
    physiotherapists: "Fisioterapeutas",
    appointments: "Citas",
    automations: "Automatizaciones",
    aiConfig: "Configuración de IA",
    aiMonitoring: "Monitor de IA",
    debugReplay: "Debug / Replay",
    operationalLogs: "Logs Operativos",
    tenantManagement: "Gestión de Clínicas",
    birthdayNotifications: "Cumpleaños",

    // Inbox tabs
    inboxTab: {
      all: "Todas",
      attention: "Requieren Atención",
      inProgress: "En Curso",
      closed: "Cerradas",
    },

    // Quick actions
    quickAction: {
      newConversation: "+ Nueva Conversación",
      scheduleAppointment: "+ Agendar Cita",
    },

    // Favorites section
    favorites: {
      title: "Favoritos",
      add: "Agregar a favoritos",
      remove: "Quitar de favoritos",
    },

    // Patients module
    patientsModule: {
      title: "Pacientes",
      searchPlaceholder: "Buscar pacientes por nombre o teléfono...",
      columns: {
        name: "Nombre",
        phone: "Teléfono",
        lastConsult: "Última Consulta",
        recordStatus: "Estado del Expediente",
      },
      recordStatusLabels: {
        complete: "Completo",
        partial: "Parcial",
        empty: "Vacío",
      },
      detail: {
        age: "Edad",
        birthDate: "Fecha de Nacimiento",
        weight: "Peso",
        height: "Estatura",
        consultReason: "Motivo de Consulta",
        painLevel: "Nivel de Dolor",
        medicalHistory: "Antecedentes Médicos",
        surgicalHistory: "Antecedentes Quirúrgicos",
        traumaticHistory: "Antecedentes Traumáticos",
        conversations: "Conversaciones Asociadas",
        editRecord: "Editar Expediente",
        deletePatient: "Eliminar Paciente",
      },
      phiToggle: "Mostrar datos sensibles (PHI)",
      newPatient: "Nuevo Paciente",
      emptyState: "No se encontraron pacientes",
      errorLoading: "Error al cargar pacientes",
      form: {
        fields: {
          name: "Nombre",
          phone: "Teléfono",
          birthDate: "Fecha de Nacimiento",
          age: "Edad",
          weight: "Peso (kg)",
          height: "Estatura (cm)",
          consultReason: "Motivo de Consulta",
          painLevel: "Nivel de Dolor (0-10)",
          medicalHistory: "Antecedentes Médicos",
          surgicalHistory: "Antecedentes Quirúrgicos",
          traumaticHistory: "Antecedentes Traumáticos",
        },
        placeholders: {
          name: "Nombre completo del paciente",
          phone: "+52 55 1234 5678",
          age: "Años",
          weight: "kg",
          height: "cm",
          consultReason: "Describa el motivo de consulta...",
          medicalHistory: "Antecedentes médicos relevantes...",
          surgicalHistory: "Cirugías previas...",
          traumaticHistory: "Lesiones o traumas previos...",
        },
        buttons: {
          create: "Crear Paciente",
          update: "Guardar Cambios",
        },
        validation: {
          required: "Este campo es obligatorio",
          phoneMinDigits: "El teléfono debe tener al menos 10 dígitos",
          birthDateFuture: "La fecha de nacimiento no puede ser futura",
          positiveNumber: "Debe ser un número positivo",
          painLevelRange: "Debe ser un número entero entre 0 y 10",
        },
        errors: {
          duplicatePhone: "Ya existe un paciente con este número de teléfono",
          submitError: "Error al guardar paciente",
        },
      },
      deleteDialog: {
        title: "Eliminar Paciente",
        description: "¿Estás seguro de que quieres eliminar a {{name}}? El paciente será removido de la lista pero el registro se conservará.",
        confirm: "Eliminar",
        cancel: "Cancelar",
        deleting: "Eliminando...",
      },
    },

    // Appointments module
    appointmentsModule: {
      title: "Citas",
      searchPlaceholder: "Buscar por nombre de paciente o teléfono...",
      columns: {
        patient: "Paciente",
        dateTime: "Fecha y Hora",
        type: "Tipo",
        status: "Estado",
        doctor: "Médico",
      },
      statusTabs: {
        upcoming: "Próximas",
        cancelled: "Canceladas",
        rescheduled: "Reprogramadas",
        noShow: "No Asistió",
        completed: "Completada",
      },
      dateTo: "Hasta",
      dateRangeFilter: "Filtrar por rango de fechas",
      detailPanel: {
        title: "Detalles de la Cita",
        notes: "Notas",
        duration: "Duración",
        location: "Ubicación",
        linkedConversation: "Conversación Asociada",
      },
      newAppointment: "Nueva Cita",
      editAppointment: "Editar",
      form: {
        fields: {
          patientName: "Nombre del Paciente",
          patientPhone: "Teléfono del Paciente",
          dateTime: "Fecha y Hora",
          type: "Tipo",
          doctor: "Médico",
          notes: "Notas",
          duration: "Duración (min)",
          location: "Ubicación",
        },
        placeholders: {
          patientName: "Nombre completo del paciente",
          patientPhone: "+52 55 1234 5678",
          type: "ej. Consulta, Seguimiento",
          doctor: "Nombre del médico",
          notes: "Notas adicionales...",
          duration: "Minutos",
          location: "Consultorio o dirección",
        },
        buttons: {
          create: "Crear Cita",
          update: "Guardar Cambios",
        },
        validation: {
          required: "Este campo es obligatorio",
          positiveNumber: "Debe ser un número positivo",
        },
        errors: {
          submitError: "Error al guardar cita",
        },
      },
      emptyState: "No se encontraron citas",
      errorLoading: "Error al cargar citas",
    },

    // Automations module
    automationsModule: {
      title: "Automatizaciones",
      columns: {
        name: "Nombre",
        type: "Tipo",
        status: "Estado",
        lastExecution: "Última Ejecución",
      },
      form: {
        title: "Configuración de Automatización",
        name: "Nombre",
        typeSelector: {
          label: "Tipo de Automatización",
          scheduledMessage: "Mensaje Programado",
          reminder: "Recordatorio",
          eventTrigger: "Trigger por Evento",
        },
        triggerEvent: "Evento Disparador",
        messageTemplate: "Plantilla de Mensaje",
        templateVariables: "Variables Disponibles",
        schedule: "Programación (cron)",
      },
      executionHistory: {
        title: "Historial de Ejecuciones",
        columns: {
          date: "Fecha",
          recipient: "Destinatario",
          deliveryStatus: "Estado de Entrega",
          error: "Error",
        },
        statusLabels: {
          delivered: "Entregado",
          failed: "Fallido",
          pending: "Pendiente",
        },
      },
      toggle: "Activa",
      createNew: "Nueva Automatización",
      editAutomation: "Editar Automatización",
      backToList: "Volver a la lista",
      triggerEvents: {
        appointmentScheduled: "Cita Agendada",
        appointmentCancelled: "Cita Cancelada",
        intakeCompleted: "Intake Completado",
        appointmentRescheduled: "Cita Reprogramada",
        noShow: "No-Show Detectado",
      },
      templateVariablesHint: "Usa {{variable}} para insertar valores dinámicos",
      templateVariablesList: {
        patientName: "Nombre del Paciente",
        appointmentDate: "Fecha de la Cita",
        doctorName: "Nombre del Médico",
      },
      typeLabels: {
        scheduled_message: "Mensaje Programado",
        reminder: "Recordatorio",
        event_trigger: "Trigger por Evento",
      },
      statusLabels: {
        active: "Activa",
        inactive: "Inactiva",
      },
      emptyState: "No hay automatizaciones configuradas",
      errorLoading: "Error al cargar automatizaciones",
      errorSaving: "Error al guardar automatización",
    },

    // AI Config module
    aiConfigModule: {
      title: "Configuración de IA",
      sections: {
        prompts: "Prompts",
        intents: "Intents",
        toneConfig: "Tono de Conversación",
      },
      prompts: {
        name: "Nombre del Prompt",
        version: "Versión",
        lastModified: "Última Modificación",
        status: "Estado",
        statusLabels: {
          active: "Activo",
          draft: "Borrador",
        },
        content: "Contenido",
        contentPlaceholder: "Ingresa el contenido del prompt para la nueva versión...",
        createVersion: "Crear Nueva Versión",
        createVersionDescription: "Escribe el contenido para la nueva versión de este prompt.",
        activateVersion: "Activar Versión",
        activate: "Activar",
        emptyState: "No hay prompts configurados",
        cancel: "Cancelar",
        create: "Crear",
      },
      intents: {
        toggle: "Habilitar Intent",
        disableConfirmation: "Deshabilitar este intent afectará el comportamiento de la IA. ¿Estás seguro?",
        impactDescription: "Impacto",
        name: "Nombre del Intent",
        description: "Descripción",
        confirmDisable: "Confirmar",
        emptyState: "No hay intents configurados",
      },
      tone: {
        presets: {
          formal: "Formal",
          friendly: "Amigable",
          neutral: "Neutro",
          custom: "Personalizado",
        },
        customInstructions: "Instrucciones Personalizadas",
        customInstructionsPlaceholder: "Ingresa instrucciones personalizadas de tono...",
        selectPreset: "Seleccionar tono predefinido",
        saved: "Configuración de tono guardada",
      },
      errorLoading: "Error al cargar configuración de IA",
      errorSaving: "Error al guardar configuración de IA",
    },

    // AI Monitoring module
    aiMonitoringModule: {
      title: "Monitor de IA",
      metrics: {
        fallbackRate: "Tasa de Fallback",
        humanInterventionRate: "Tasa de Intervención Humana",
        averageConfidence: "Confianza Promedio",
      },
      periodSelector: {
        today: "Hoy",
        "7days": "Últimos 7 Días",
        "30days": "Últimos 30 Días",
        custom: "Rango Personalizado",
      },
      trendChart: {
        fallback: "Tendencia de Fallback",
        interventions: "Tendencia de Intervenciones",
        confidence: "Tendencia de Confianza",
      },
      lowConfidenceTable: {
        title: "Conversaciones de Baja Confianza",
        columns: {
          conversation: "Conversación",
          phone: "Teléfono",
          avgConfidence: "Confianza Prom.",
          messages: "Mensajes",
          lastActivity: "Última Actividad",
        },
        viewReplay: "Ver Replay",
      },
      emptyState: "No hay datos de monitoreo disponibles",
      errorLoading: "Error al cargar métricas de IA",
    },

    // Operational Logs module
    operationalLogsModule: {
      title: "Logs Operativos",
      columns: {
        timestamp: "Fecha y Hora",
        type: "Tipo",
        severity: "Severidad",
        description: "Descripción",
        entity: "Entidad",
        resolution: "Resolución",
      },
      eventTypes: {
        messageFailure: "Fallo de Mensaje",
        dlq: "Cola de Mensajes Muertos",
        retry: "Reintento",
        integrationError: "Error de Integración",
        patientCreated: "Paciente Creado",
        patientUpdated: "Paciente Actualizado",
        patientDeleted: "Paciente Eliminado",
        appointmentCreated: "Cita Creada",
        appointmentUpdated: "Cita Actualizada",
        appointmentDeleted: "Cita Eliminada",
        leadCreated: "Lead Creado",
        leadUpdated: "Lead Actualizado",
        leadDeleted: "Lead Eliminado",
        leadAssigned: "Lead Asignado",
        leadStageChanged: "Etapa de Lead Cambiada",
        userLogin: "Inicio de Sesión",
        userLogout: "Cierre de Sesión",
        userCreated: "Usuario Creado",
        userUpdated: "Usuario Actualizado",
        userDeleted: "Usuario Eliminado",
        templateCreated: "Plantilla Creada",
        templateUpdated: "Plantilla Actualizada",
        templateDeleted: "Plantilla Eliminada",
        automationCreated: "Automatización Creada",
        automationUpdated: "Automatización Actualizada",
        automationToggled: "Automatización Activada/Desactivada",
        automationDeleted: "Automatización Eliminada",
        propertyCreated: "Propiedad Creada",
        propertyUpdated: "Propiedad Actualizada",
        propertyDeleted: "Propiedad Eliminada",
        visitCreated: "Visita Creada",
        visitUpdated: "Visita Actualizada",
        visitDeleted: "Visita Eliminada",
        conversationCreated: "Conversación Creada",
        conversationUpdated: "Conversación Actualizada",
        conversationDeleted: "Conversación Eliminada",
        messageSent: "Mensaje Enviado",
        roleCreated: "Rol Creado",
        roleUpdated: "Rol Actualizado",
        roleDeleted: "Rol Eliminado",
        permissionUpdated: "Permisos Actualizados",
        tenantCreated: "Tenant Creado",
        tenantUpdated: "Tenant Actualizado",
        tenantDeleted: "Tenant Eliminado",
        settingsChanged: "Configuración Cambiada",
        aiConfigUpdated: "Configuración IA Actualizada",
        scheduleCreated: "Horario Creado",
        scheduleUpdated: "Horario Actualizado",
        scheduleDeleted: "Horario Eliminado",
        widgetConfigUpdated: "Configuración de Widget Actualizada",
        workdayConfigUpdated: "Jornada Laboral Actualizada",
        systemAction: "Acción del Sistema",
      },
      severityLevels: {
        info: "Información",
        warning: "Advertencia",
        error: "Error",
      },
      filters: {
        byType: "Filtrar por Tipo",
        bySeverity: "Filtrar por Severidad",
        byDateRange: "Filtrar por Rango de Fechas",
      },
      detailPanel: {
        title: "Detalles del Evento",
        payload: "Detalles",
        stackTrace: "Registro Técnico",
        metadata: "Información Adicional",
        technicalId: "ID Técnico",
      },
      entityTypes: {
        patient: "Paciente",
        appointment: "Cita",
        schedule: "Horario",
        lead: "Prospecto",
        user: "Usuario",
        template: "Plantilla",
        automation: "Automatización",
        property: "Propiedad",
        visit: "Visita",
        conversation: "Conversación",
        message: "Mensaje",
        role: "Rol",
        permission: "Permisos",
        tenant: "Clínica",
        settings: "Configuración",
        ai_config: "Configuración IA",
        workday_config: "Jornada Laboral",
        widget_config: "Widget",
        system: "Sistema",
        unknown: "Registro",
      },
      payloadLabels: {
        name: "Nombre",
        firstName: "Nombre",
        lastName: "Apellido",
        email: "Correo Electrónico",
        phone: "Teléfono",
        date: "Fecha",
        time: "Hora",
        startTime: "Hora de Inicio",
        endTime: "Hora de Fin",
        status: "Estado",
        patientName: "Nombre del Paciente",
        patientId: "Paciente",
        doctorName: "Doctor",
        reason: "Motivo",
        notes: "Notas",
        description: "Descripción",
        type: "Tipo",
        title: "Título",
        active: "Activo",
        enabled: "Habilitado",
        dayOfWeek: "Día de la Semana",
        duration: "Duración",
        location: "Ubicación",
        specialty: "Especialidad",
        role: "Rol",
      },
      resolved: "Resuelto",
      unresolved: "Sin Resolver",
      emptyState: "No se encontraron eventos operativos",
      errorLoading: "Error al cargar logs operativos",
    },

    // Tenant Management module
    tenantManagementModule: {
      title: "Gestión de Clínicas",
      columns: {
        clinicName: "Nombre de la Clínica",
        status: "Estado",
        plan: "Plan",
        createdDate: "Fecha de Creación",
      },
      statusLabels: {
        active: "Activo",
        inactive: "Inactivo",
      },
      configSections: {
        generalInfo: "Información General",
        operationalConfig: "Configuración Operativa",
        integrations: "Integraciones",
      },
      generalInfo: {
        name: "Nombre de la Clínica",
        logo: "Logo",
        primaryColor: "Color Primario",
        secondaryColor: "Color Secundario",
      },
      operationalConfig: {
        businessHours: "Horario de Atención",
        timezone: "Zona Horaria",
        language: "Idioma",
      },
      integrations: {
        whatsapp: "WhatsApp",
        whatsappPhone: "Número de WhatsApp",
        email: "Email",
        emailSender: "Dirección de Email del Remitente",
        enabled: "Habilitado",
        disabled: "Deshabilitado",
      },
      brandingPreview: "Vista Previa de Marca",
      toggle: "Confirmar",
      toggleConfirmation: "¿Estás seguro de que deseas cambiar el estado de esta clínica?",
      emptyState: "No se encontraron clínicas",
      errorLoading: "Error al cargar clínicas",
      errorSaving: "Error al guardar configuración de la clínica",
    },

    // Search categories
    searchCategories: {
      conversations: "Conversaciones",
      patients: "Pacientes",
      appointments: "Citas",
      navigation: "Navegación",
      emptyState: "No se encontraron resultados para tu búsqueda",
    },

    // Replay/Debug panel
    replayPanel: {
      title: "Debug / Replay",
      searchPlaceholder: "Buscar por ID de conversación o teléfono...",
      recentConversations: "Conversaciones Recientes",
      noConversationSelected: "Selecciona una conversación para iniciar la depuración",
      noRecentConversations: "No hay conversaciones recientes disponibles",
      noSearchResults: "No hay conversaciones que coincidan con tu búsqueda",
      conversationId: "ID",
      phone: "Teléfono",
      lastActivity: "Última Actividad",
    },

    // Common error/empty states
    errorLoading: "Error al cargar datos",
    retry: "Reintentar",
    noResults: "Sin resultados",
    loading: "Cargando...",
  },
} as const

export function t(key: string, locale: Locale, params?: Record<string, string | number>): string {
  const keys = key.split(".")
  let value: any = translations[locale]

  for (const k of keys) {
    if (value && typeof value === "object") {
      value = value[k]
    } else {
      return key
    }
  }

  if (typeof value === "string") {
    if (params) {
      return value.replace(/\{(\w+)\}/g, (_, param) => String(params[param] ?? `{${param}}`))
    }
    return value
  }

  return key
}
